import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Video, User, Globe, Calendar, Users, Monitor, Shield, Search, BarChart3, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { usePrefs, useT } from "@/lib/prefs";
import { Logo, Button } from "@/components/brand";
import { VideoHero } from "@/components/VideoHero";
import { CategoryTile } from "@/components/cards";


export default function Welcome() {
  const t = useT();
  const navigate = useNavigate();
  const { language, currency } = usePrefs();
  const { data: cats } = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories", { token: null }) });

  return (
    <div className="min-h-screen bg-white" data-testid="welcome-screen">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-divider">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 h-16 flex items-center gap-3">
          <Logo size={30} />
          <div className="flex-1" />
          <Link to="/preferences" data-testid="welcome-language-button" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill bg-surface2 border border-line font-body font-semibold text-xs text-ink">
            <Globe size={16} />{language.toUpperCase()} · {currency}
          </Link>
          <Link to="/login" className="hidden sm:inline"><Button testID="welcome-login-top" title={t("welcome.login")} variant="dark" small /></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center pt-10 lg:pt-16">
        <div className="animate-fade-up">
          <p className="font-body font-semibold text-[11px] tracking-widest text-brand">{t("welcome.kicker")}</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink mt-3">
            {t("welcome.h1a")}<br /><span className="text-brand">{t("welcome.h1b")}</span>
          </h1>
          <p className="font-body text-base text-mutedgrey mt-5 max-w-lg">{t("welcome.lead")}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button testID="welcome-find-class" title={t("welcome.findClass")} icon={ArrowRight} onClick={() => navigate("/register")} />
            <Button testID="welcome-find-trainer" title={t("welcome.findTrainer")} variant="outline" onClick={() => navigate("/register")} />
          </div>
          <div className="flex gap-8 mt-8">
            {[[Video, "Live Interactive Classes"], [User, "1-on-1 Personal Training"], [Globe, "Train Anywhere"]].map(([Icon, label], i) => (
              <div key={i} className="flex flex-col items-center text-center gap-1.5 flex-1">
                <Icon size={22} className="text-brand" />
                <span className="font-body font-semibold text-xs text-ink">{label}</span>
              </div>
            ))}
          </div>
        </div>
        <VideoHero />
      </section>

      {/* Feature band */}
      <section className="mt-16 bg-brand-soft">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[[Calendar, "Wide Range of Classes", "Yoga, HIIT, Strength, Pilates, Dance and more."], [Users, "Certified Trainers", "Train with experienced and verified professionals."], [Monitor, "Live & Interactive", "See and be seen. Get real-time feedback."], [Shield, "Safe & Secure", "Easy booking, secure payments and a trusted community."]].map(([Icon, title, sub], i) => (
            <div key={i} className="flex flex-col items-center text-center gap-2">
              <Icon size={26} className="text-brand" />
              <div className="font-display text-[15px] text-ink">{title}</div>
              <div className="font-body text-xs text-mutedgrey leading-snug">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <h2 className="font-display text-3xl text-ink tracking-tight">Explore Our Classes</h2>
        <p className="font-body text-mutedgrey mt-1 mb-5">Find the right class for your goals.</p>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
          {(cats ?? []).map((c) => <CategoryTile key={c.key} name={c.name} image={c.image} icon={c.icon} testID={`welcome-cat-${c.key}`} onClick={() => navigate("/register")} />)}
        </div>
      </section>

      {/* How it works (dark band) */}
      <section className="bg-ink text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
          <h2 className="font-display text-3xl tracking-tight">How Sofit Works</h2>
          <p className="font-body text-white/70 mt-1">Get started in just a few steps.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-8">
            {[[Search, "1. Discover", "Browse classes or find a trainer."], [Calendar, "2. Book", "Choose your time and pay securely."], [Video, "3. Join Live", "Attend your class or private session online."], [BarChart3, "4. Get Results", "Stay consistent and reach your goals."]].map(([Icon, title, sub], i) => (
              <div key={i} className="flex flex-col items-center text-center gap-2">
                <div className="w-12 h-12 rounded-full bg-brand flex items-center justify-center"><Icon size={22} className="text-white" /></div>
                <div className="font-display text-[15px]">{title}</div>
                <div className="font-body text-xs text-white/70 leading-snug">{sub}</div>
              </div>
            ))}
          </div>
          <div className="font-accent text-3xl text-brand text-right mt-8 -rotate-3">Fitness Without Limits</div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <div className="max-w-md mx-auto flex flex-col gap-3">
          <Button testID="welcome-signup" title={t("welcome.signup")} onClick={() => navigate("/register")} full />
          <Button testID="welcome-login" title={t("welcome.login")} variant="dark" onClick={() => navigate("/login")} full />
        </div>
      </section>
    </div>
  );
}
